import { convertHourToTime, Days } from "@ciq/shared/utils/date/manager"
import React from "react"
import { HoverTooltip } from "@ciq/shared/components/tooltip"
import { useFilterQueryParams } from "../../hooks/filter_query_params"
import { isEmptyArray } from "../../../../utils/array"
import { EmptyView } from "@ciq/shared/components/views"
import { NO_DATA_AVAILABLE_MESSAGE } from "@ciq/shared/utils/const"

const DEFAULT_COLOR = "var(--neutral-color-150)"

function HourIndicatorLine() {
  const { hour } = useFilterQueryParams()

  if (hour == null || hour < 0 || hour > 23) {
    return null
  }

  // Calculate position: center the line in the hour column
  const leftPosition = `${((hour + 0.5) / 24) * 100}%`

  return <div className="utilization-graph-hour-indicator" style={{ left: leftPosition }} />
}

function TooltipContent({ title, info }) {
  return (
    <div className="column-flex gap-200">
      <span className="utilization-graph-popup-title">{title}</span>
      <div className="row-flex gap-200 align-items-center">
        <p className="metric-color" style={{ backgroundColor: info?.color || DEFAULT_COLOR }} />
        <span>{info.label}</span>
      </div>
    </div>
  )
}

const ColorInfo = color => {
  if (color == null) {
    return {
      cursor: "default",
      readonly: true,
      color: DEFAULT_COLOR,
    }
  }

  return {
    cursor: "pointer",
    readonly: false,
    color,
  }
}

function Color({ color, opacity }) {
  const info = ColorInfo(color)

  return (
    <div
      className="utilization-graph-color-container"
      aria-readonly={info.readonly}
      style={{ cursor: info.cursor }}
    >
      <p style={{ backgroundColor: "white" }} />
      <p style={{ backgroundColor: `hsl(from ${info.color} h s l / ${opacity ?? 1})` }} />
    </div>
  )
}

function YLabels({ labels }) {
  if (isEmptyArray(labels)) {
    return null
  }

  return (
    <div className="utilization-graph-y-labels">
      {labels.map((label, i) => (
        <YLabel key={`label-${i}`} label={label} />
      ))}
    </div>
  )
}

function YLabel({ label }) {
  return (
    <div>
      <span>{label}</span>
    </div>
  )
}

function XLabels({ labels }) {
  if (isEmptyArray(labels)) {
    return null
  }

  return (
    <div className="utilization-graph-x-labels">
      {labels.map((label, i) => (
        <XLabel key={`label-${i}`} label={label} />
      ))}
    </div>
  )
}

function XLabel({ label }) {
  const hasLabel = !!label

  return (
    <div className="utilization-graph-x-label-container">{hasLabel && <span>{label}</span>}</div>
  )
}

export function UtilizationGraphByDay({ data, legend }) {
  const { show, hasData, datasource } = data

  if (!show) {
    return null
  }

  if (!hasData) {
    return <EmptyView>{NO_DATA_AVAILABLE_MESSAGE}</EmptyView>
  }

  const items = []

  for (const day of Days) {
    for (let hour = 0; hour < 24; hour++) {
      const info = datasource.getColorInfoForMetric(day.value, hour)
      const tooltipContent = info ? (
        <TooltipContent title={`${day.shortName}, ${convertHourToTime(hour)}`} info={info} />
      ) : null
      items.push(
        <div key={`color-${day.value}-${hour}`} className="utilization-graph-popup">
          <HoverTooltip tooltip={tooltipContent} className="flex-grow">
            <div className="column-flex full-height utilization-graph-hover-effect">
              <Color color={info.color} opacity={info.opacity} />
            </div>
          </HoverTooltip>
        </div>
      )
    }
  }

  return (
    <div className="utilization-graph utilization-graph-dow">
      <YLabels labels={datasource.getYLabels()} />
      <div className="utilization-graph-dow-container">
        {items}
        <HourIndicatorLine />
      </div>
      <p />
      <XLabels labels={datasource.getXLabels()} />
      <p />
      <Legend datasource={legend} />
    </div>
  )
}

function HourBar({ hour, info, isCurrent }) {
  const tooltipContent = info ? (
    <TooltipContent title={convertHourToTime(hour)} info={info} />
  ) : null

  return (
    <div className="utilization-graph-hour-bar-container">
      <div className="utilization-graph-popup">
        <HoverTooltip tooltip={tooltipContent} className="flex-grow">
          <div
            className="column-flex full-height utilization-graph-hover-effect"
            aria-selected={isCurrent}
          >
            <div className="utilization-graph-hour-bar">
              <div
                className="utilization-graph-hour-bar-render"
                style={{ height: info.percentage, backgroundColor: info.color }}
              />
            </div>
          </div>
        </HoverTooltip>
      </div>
    </div>
  )
}

function GraphLines({ yLabels }) {
  const lines = []

  if (!isEmptyArray(yLabels)) {
    for (let i = 0; i < yLabels.length; i++) {
      lines.push(<p key={`hour-bar-${i}`} />)
    }
  }

  return <div className="utilization-graph-hour-bars">{lines}</div>
}

function CapacityLine({ capacity }) {
  if (capacity == null) {
    return null
  }

  return (
    <div className="utilization-graph-hour-capacity">
      <div style={{ height: capacity }} />
    </div>
  )
}

export function UtilizationGraphByHour({ data, legend }) {
  const { show, hasData, datasource } = data
  const { hour: currentHour } = useFilterQueryParams()

  if (!show) {
    return null
  }

  if (!hasData) {
    return <EmptyView>{NO_DATA_AVAILABLE_MESSAGE}</EmptyView>
  }

  const bars = []
  for (let hour = 0; hour < 24; hour++) {
    const info = datasource.getColorInfoForMetric(hour)
    bars.push(
      <HourBar key={`hour-${hour}`} hour={hour} info={info} isCurrent={hour === currentHour} />
    )
  }

  const yLabels = datasource.getYLabels()

  return (
    <div className="utilization-graph utilization-graph-hour">
      <YLabels labels={yLabels} />
      <div className="utilization-graph-hour-container">
        <GraphLines yLabels={yLabels} />
        <CapacityLine capacity={datasource.getCapacity()} />
        {bars}
      </div>
      <p />
      <XLabels labels={datasource.getXLabels()} />
      <p />
      <Legend datasource={legend} />
    </div>
  )
}

const Legend = ({ datasource }) => {
  // Early return if legend should not be shown
  if (!datasource?.show) {
    return null
  }

  // Check if we have actual legend data to show
  const colors = []

  for (let i = 0; i < datasource.numberOfLegendValues(); i++) {
    const info = datasource.getColorInfoForIndex(i)
    const tooltipContent = info ? <TooltipContent title={info.title} info={info} /> : null
    colors.push(
      <div key={`color-${i}`} className="utilization-graph-popup">
        <HoverTooltip tooltip={tooltipContent} className="flex-grow">
          <div className="column-flex full-height utilization-graph-hover-effect">
            <Color color={info.color} opacity={info.opacity} />
          </div>
        </HoverTooltip>
      </div>
    )
  }

  if (!colors.length) {
    return null
  }

  const { start, end } = datasource.getLegendAnchorText()

  return (
    <div className="utilization-graph-legend">
      <div className="utilization-graph-legend-content">
        <span className="text-align-right">{start}</span>
        <div className="utilization-graph-legend-colors">{colors}</div>
        <span>{end}</span>
      </div>
    </div>
  )
}
